import Vue from 'vue';
import Vuex from 'vuex';

import { format } from 'date-fns';

Vue.use(Vuex);

// Only update filters here so one reference to them is maintained
// a function returns a new object every time so can use this for reset
function initialFilterState() {
  return {
    day: 'any',
    days: [],
    availableDays: [],
    availableTime: 'any',
    availableTimes: [],
    availableAges: [],
    selectedAge: 'any',
    className: 'any',
    venueName: 'any',
    showCamps: 'true',
    showTerms: 'true',
    onlyAvailableRuns: false,
    age: {
      value: '',
      unit: 'years',
    },
    time: {
      morning: 'false',
      afternoon: 'false',
      evening: 'false',
    },
    distance: {
      postcode: '',
      latitude: '',
      longitude: '',
      radius: 5,
    },
  };
}

// VUEX Store
export default new Vuex.Store({
  state: {
    loading: true,
    terms: [
      {
        runs: [],
      },
    ],
    camps: [],
    filters: initialFilterState(),
  },

  actions: {
    async GET_SCHEDULE_DATA(context) {
      const url = '/api/v4/public/schedule/current';
      try {
        const request = new Request(url, {
          method: 'GET',
          credentials: 'same-origin',
        });

        const response = await fetch(request);
        const json = await response.json();

        return context.commit('UPDATE_SCHEDULE_INFO', json);
      } catch (error) {
        console.error(error);
      }
    },

    CHANGE_DAY_FILTER(context, payload) {
      context.commit('UPDATE_DAY_FILTER', payload);
    },

    CHANGE_DAYS_FILTER(context, payload) {
      context.commit('UPDATE_DAYS_FILTER', payload);
    },

    CHANGE_CLASS_FILTER(context, payload) {
      context.commit('UPDATE_CLASS_FILTER', payload);
    },
    CHANGE_VENUE_FILTER(context, payload) {
      context.commit('UPDATE_VENUE_FILTER', payload);
    },
  },

  mutations: {
    UPDATE_SCHEDULE_INFO(state, payload) {
      state.terms = payload.terms;
      state.camps = payload.camps;
      state.loading = false;
    },

    UPDATE_LOADING_STATE(state, payload) {
      state.loading = payload;
    },

    RESET_FILTERS(state, payload) {
      Object.assign(state.filters, initialFilterState());
    },
    UPDATE_DAY_FILTER(state, payload) {
      state.filters.day = payload;
    },

    UPDATE_DAYS_FILTER(state, payload) {
      state.filters.days = payload;
    },

    UPDATE_CLASS_FILTER(state, payload) {
      state.filters.className = payload;
    },
    UPDATE_VENUE_FILTER(state, payload) {
      state.filters.venueName = payload;
    },
    UPDATE_AVAILABLE_RUNS_FILTER(state, payload) {
      state.filters.onlyAvailableRuns = payload;
    },
    UPDATE_AGE_FILTER_VALUE(state, payload) {
      state.filters.age.value = payload;
    },
    UPDATE_AGE_FILTER_UNIT(state, payload) {
      state.filters.age.unit = payload;
    },

    UPDATE_TIME_FILTER(state, payload) {
      state.filters.time[payload.time] = payload.value;
    },
    UPDATE_POSTCODE(state, payload) {
      state.filters.distance.postcode = payload;
    },
    UPDATE_RADIUS(state, payload) {
      state.filters.distance.radius = payload;
    },

    UPDATE_LAT_LONG(state, payload) {
      state.filters.distance.latitude = payload.latitude;
      state.filters.distance.longitude = payload.longitude;
    },

    UPDATE_AVAILABLE_TIME_FILTER(state, payload) {
      state.filters.availableTime = payload;
    },

    UPDATE_SELECTED_AGE(state, payload) {
      state.filters.selectedAge = payload;
    },

    UPDATE_SHOW_TERMS_FILTER(state, payload) {
      state.filters.showTerms = payload;
    },

    UPDATE_SHOW_CAMPS_FILTER(state, payload) {
      state.filters.showCamps = payload;
    },
  },

  getters: {
    terms(state) {
      return state.terms;
    },
    camps(state) {
      return state.camps;
    },

    showTerms(state) {
      return state.filters.showTerms;
    },

    showCamps(state) {
      return state.filters.showCamps;
    },

    loading(state) {
      return state.loading;
    },

    classes(state) {

      let classes = [];
      const terms = Object.values(state.terms);
      terms.forEach((term) => {
        const runs = Object.values(term.runs);
        runs.forEach((run) => {
          const { className } = run;
          classes = [...new Set([...classes, className])];
        })
      });

      for (var i = 0; i < state.camps.length; i++) {
        if (classes.indexOf(state.camps[i].className) == -1) {
          classes.push(state.camps[i].className);
        }
      }

      return classes;
    },
    venues(state) {
      let venues = [];

      const terms = Object.values(state.terms);
      terms.forEach((term) => {
        const runs = Object.values(term.runs);
        runs.forEach((run) => {
          const { venueName } = run;
          venues = [...new Set([...venues, venueName])];
        })
      });


      for (var i = 0; i < state.camps.length; i++) {
        if (venues.indexOf(state.camps[i].venueName) == -1) {
          venues.push(state.camps[i].venueName);
        }
      }

      return venues;
    },

    day(state) {
      return state.filters.day;
    },

    days(state) {
      return state.filters.days;
    },

    availableDays(state) {
      let availableDays = [];
      const terms = Object.values(state.terms);
      terms.forEach((term) => {
        availableDays = [...new Set([...availableDays, ...term.days])];
      });

      return availableDays;
    },

    availableTimes(state) {
      const availableTimes = [];
      const terms = Object.values(state.terms);
      terms.forEach((term) => {
        const runs = Object.values(term.runs);
        runs.forEach((run) => {
          const times = Object.values(run.classTimes);
          times.forEach( (classTime) => {
            const { starttime } = classTime;
            const startHour = parseInt(starttime.hours, 10);
            let time;
            if (startHour < 12) {
              time = 'Morning';
            } else if (startHour >= 12 && startHour < 16) {
              time = 'Afternoon';
            } else if (startHour >= 16) {
              time = 'Evening';
            }

            if (availableTimes.indexOf(time) == -1) {
              availableTimes.push(time);
            }
          })
        })


      });

      return availableTimes;
    },

    availableTime(state) {
      return state.filters.availableTime;
    },

    availableAges(state) {
      const availableAges = [];

      for (let i = 0; i < state.terms.length; i++) {
        for (let j = 0; j < state.terms[i].runs.length; j++) {
          const ageClassIsFrom = state.terms[i].runs[j].ageFrom;
          const ageClassIsTo = state.terms[i].runs[j].ageTo;

          // FIX!!
          if (ageClassIsFrom < 24) {
            var ageFromFormatted = `${ageClassIsFrom} mths`;
          } else if (ageClassIsFrom % 12 == 0) {
            var ageFromFormatted = `${ageClassIsFrom / 12}yrs`;
          } else {
            var ageFromFormatted = `${Math.floor(ageClassIsFrom / 12)
            }yrs ${
              ageClassIsFrom % 12
            }${ageClassIsFrom > 1 ? 'mths' : 'mth'}`;
          }

          if (ageClassIsTo < 24) {
            var ageToFormatted = `${ageClassIsTo} mths`;
          } else if (ageClassIsFrom % 12 == 0) {
            var ageToFormatted = `${ageClassIsTo / 12}yrs`;
          } else {
            var ageToFormatted = `${Math.floor(ageClassIsTo / 12)
            }yrs ${
              ageClassIsTo % 12
            }${ageClassIsTo > 1 ? 'mths' : 'mth'}`;
          }

          const age = `${ageFromFormatted} - ${ageToFormatted}`;

          if (availableAges.indexOf(age) == -1) {
            availableAges.push(age);
          }
        }
      }

      return availableAges;
    },

    className(state) {
      return state.filters.className;
    },
    venueName(state) {
      return state.filters.venueName;
    },
    onlyAvailableRuns(state) {
      return state.filters.onlyAvailableRuns;
    },

    ageFilterValue(state) {
      return state.filters.age.value;
    },

    ageFilterUnit(state) {
      return state.filters.age.unit;
    },

    timeFilterMorning(state) {
      return state.filters.time.morning;
    },
    timeFilterAfternoon(state) {
      return state.filters.time.afternoon;
    },
    timeFilterEvening(state) {
      return state.filters.time.evening;
    },

    postcode(state) {
      return state.filters.distance.postcode;
    },

    latitude(state) {
      return state.filters.distance.latitude;
    },

    longitude(state) {
      return state.filters.distance.longitude;
    },

    radius(state) {
      return state.filters.distance.radius;
    },

    selectedAge(state) {
      return state.filters.selectedAge;
    },
  },
});
